import { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../TerminalInit';
import { Contact } from '../../api/contacts';
import { useSpotLayout } from '../../api/spotLayouts';
import { useSpot } from '../../api/spots';
import { TerminalWorkflowType } from '../../api/terminals';
import ActionCard from '../../components/domain/ActionCard';
import ActionCardGridElement from '../../components/elements/ActionCardGridElement';
import { useAppDispatch } from '../../hooks/redux';
import useReadyForUpdate from '../../hooks/useReadyForUpdate';
import { backward } from '../../redux/ViewSlice';
import badgeNfc from '../../static/nike/badge-nfc-alt.png';
import BackAndHomeNavigationButtons from '../../views/common/BackAndHomeNavigationButtons';
import BaseView from '../../views/common/BaseView';
import LoadingView from '../../views/common/LoadingView';
import LanguagePicker from '../../views/elements/LanguagePicker';
import ViewTitle from '../../views/nike/ViewTitle';
import { AccessControllAction } from '../AccessControllAction';
import AdminPanelAction from '../Actions/Admin/AdminPanelAction';
import DropoffWorkflowAction from '../Actions/Dropoff/DropoffWorkflow';
import PickupWorkflowAction from '../Actions/Pickup/PickupWorkflow';
import QRHelpAction from '../Actions/Scanner/QRHelpAction';
import CheckReturnsAction from '../Actions/Vending/Management/CheckReturnsAction';
import EnterProductCodeAction from '../Actions/Vending/Management/EnterProductCodeAction';
import ManualStockRefillAction from '../Actions/Vending/Management/ManualStockRefillAction';
import ManualStockRefillWithoutReportNumberAction from '../Actions/Vending/Management/ManualStockRefillWithoutReportNumberAction';
import CollectWorkflowAction from '../Actions/Vending/Shop/CollectAction';
import ReturnItemsAction from '../Actions/Vending/Shop/ReturnItemsAction';
import ResetBadgeAction from '../Actions/Vending/badgeRegistration/ResetBadgeAction';
import NikeHelpWorkflowAction from '../Actions/Vending/help/NikeHelpWorkflowAction';
import SlotIssuesAction from '../Actions/management/SlotIssuesAction';
import TransactionHistoryAction from '../Actions/transactionManagement/TransactionHistoryAction';
import DelhaizeRecycleDropoffAction from '../UserSpecificActions/DelhaizeRecycleDropoffAction';
import DelhaizeRecyclePickupAction from '../UserSpecificActions/DelhaizeRecyclePickupAction';
import IMECFABDropoff from '../UserSpecificActions/IMECFABDropoff';
import LaundryVanendeRoxyAction from '../UserSpecificActions/LaundryVanendeRoxyDropoffAction';
import NikeLaundryDropoffAction from '../UserSpecificActions/NikeLaundryDropoffAction';
import NikeLaundryPickupAction from '../UserSpecificActions/NikeLaundryPickupAction';
import ContentManagementAction from '../contentManagement/ContentManagementAction';
import ItmseLoginAction from '../social-login/ItsmeLogin';
import ProductDiff from '../warehouse/ProductDiff';
import { WarehouseDropoffAction } from '../warehouse/WarehouseDropoff';
import { WarehouseCheckoutAction } from '../warehouse/checkout/WarehouseCheckoutAction';

interface AuthenticatedContactWorkflowProps {
    onHome: () => void;
    onLogout?: () => void;
    onInactivity?: () => void;
    contact?: Contact;
    onSelectAction: (action: ActionCard) => void;
    onHowDoesItWork?: () => void;
    workflow?: TerminalWorkflowType;

    authenticateContact?: (contact: Contact | undefined) => void;
}

const AuthenticatedContactWorkflow = (props: AuthenticatedContactWorkflowProps) => {
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const backHomeNav = props.contact ? <BackAndHomeNavigationButtons onLogout={props.onLogout ? props.onLogout : props.onInactivity} /> : <LanguagePicker />;
    const intl = useIntl();
    const dispatch = useAppDispatch();

    // Signal that we are ready for update when the no user us logged in and the waiting period is over.
    useReadyForUpdate({ delayMs: props.contact ? -1 : import.meta.env.VITE_READY_FOR_UPDATE_TIMEOUT }); // -1 disables ready for update

    const onBack = () => {
        dispatch(backward());
        props.onHome();
    };

    const onSelect = (action: ActionCard) => {
        if (action.onAction) action.onAction();
        else props.onSelectAction(action);
    };

    if (!spot) {
        return (
            <LoadingView
                onHome={props.onHome}
                onInactivity={props.onInactivity}
            />
        );
    }

    return (
        <BaseView
            onInactivity={props.contact ? props.onInactivity : undefined}
            navbarItems={backHomeNav}>
            <ViewTitle>
                {!props.contact ? (
                    <FormattedMessage
                        id='views.PuDo.HomeView.WelcomeTitle'
                        description='The welcome title message on the PuDo home screen'
                        defaultMessage='Welcome to {spotName}'
                        values={{
                            spotName:
                                spot.name !== '' ? (
                                    spot.name
                                ) : (
                                    <FormattedMessage
                                        id='views.PuDo.HomeView.WelcomeTitleNoSpotName'
                                        description='The text used as spot name in the welcome title message if the spot has no name. Should be read in combination with message id views.PuDo.HomeView.WelcomeTitle.'
                                        defaultMessage='the DistriSPOT'
                                    />
                                ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id='views.AuthorisedContactWorkflow.UserActionsView.title'
                        description='The title on the UserActionsView.'
                        defaultMessage='Welcome {name}'
                        values={{
                            name: props.contact.first_name + ' ' + props.contact.last_name,
                        }}
                    />
                )}
            </ViewTitle>
            {props.workflow === TerminalWorkflowType.WAREHOUSE && !props.contact && <ProductDiff />}
            {(props.workflow === TerminalWorkflowType.IMES_DEXIS_NIKE ||
                props.workflow === TerminalWorkflowType.DELHAIZE ||
                props.workflow === TerminalWorkflowType.VENDING) &&
                !props.contact && (
                    <div className='row'>
                        <div className='col-12 col-xl-6 d-flex justify-content-center'>
                            <img
                                src={badgeNfc}
                                className='dashboard-scan-image mb-3 filter-primary'
                                alt={'NFC badge'}
                            />
                        </div>
                        <div className='col-12 col-xl-6 d-flex flex-column justify-content-center align-items-center'>
                            <p className='text-center'>
                                <FormattedMessage
                                    id='views.HomeView.ScanMessage'
                                    description='The message home screen encouraging the user to scan'
                                    defaultMessage='Please scan your badge to continue.'
                                />
                            </p>
                            {props.workflow === TerminalWorkflowType.IMES_DEXIS_NIKE ? (
                                <button
                                    className='btn btn-outline-primary rounded-pill'
                                    onClick={props.onHowDoesItWork}>
                                    <i className='bi bi-exclamation-circle me-2' />
                                    <FormattedMessage
                                        id='views.nike.HomeView.HelpButton'
                                        description='The button on the Nike home screen for the user to get an introduction on how to scan.'
                                        defaultMessage='How does it work?'
                                    />
                                </button>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                )}
            <ActionCardGridElement
                actions={[
                    CollectWorkflowAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    ReturnItemsAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //IMEC
                    IMECFABDropoff({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                        onSelect: onSelect,
                        dispatch: dispatch,
                    }),

                    //PUDO
                    DropoffWorkflowAction({
                        contact: props.contact,
                        onBack: onBack,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                        authenticateContact: props.authenticateContact,
                    }),
                    PickupWorkflowAction({
                        contact: props.contact,
                        onBack: onBack,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                        authenticateContact: props.authenticateContact,
                    }),
                    QRHelpAction({
                        contact: props.contact,
                    }),

                    //NEW VENDING
                    ManualStockRefillAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    ManualStockRefillWithoutReportNumberAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //VENDING
                    NikeHelpWorkflowAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    ResetBadgeAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    EnterProductCodeAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    ContentManagementAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    CheckReturnsAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    TransactionHistoryAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    SlotIssuesAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    WarehouseDropoffAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    WarehouseCheckoutAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    AccessControllAction({
                        contact: props.contact,
                        onBack: onBack,
                        onHome: props.onHome,
                        onInactivity: props.onLogout,
                    }),
                    NikeLaundryDropoffAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    NikeLaundryPickupAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //VAN ENDE ROXY
                    LaundryVanendeRoxyAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //DELHAIZE
                    DelhaizeRecycleDropoffAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),
                    DelhaizeRecyclePickupAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //ALL
                    AdminPanelAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                    }),

                    //SOCIAL LOGIN
                    ItmseLoginAction({
                        contact: props.contact,
                        onBack: props.onHome,
                        onHome: props.onHome,
                        onInactivity: props.onInactivity,
                        authenticateContact: props.authenticateContact,
                    }),
                ]}
                onSelectAction={onSelect}
            />
        </BaseView>
    );
};

export default AuthenticatedContactWorkflow;
